import type { ImageProps } from '@archipro-design/aria';
import { Image } from '@archipro-design/aria';
import React, { useRef, useState } from 'react';
import PaintPickerShellV2 from '~/modules/paint-picker-new/component/paint-picker-shell/PaintPickerShell';
import PaintPickerMobileShellV2 from '~/modules/paint-picker-new/component/paint-picker-shell/PaintPickerMobileShell';
import { useFeatureFlaggedPaintPickerContext } from '@modules/paint-picker-new/hooks/useFeatureFlaggedPaintPickerContext';
import { useLogger } from '@archipro-website/logger/client';
import { Image as RoccoImage } from '@rocco/components/image';

type ImageWithPaintPickerProps = ImageProps & {
    useRoccoImage?: boolean;
    rel?: string;
};

const ImageWithPaintPicker = (props: ImageWithPaintPickerProps) => {
    const {
        pickerContext: {
            state: {
                paintPickerActive,
                isEnabled,
                isPicking,
                activeImageSrc,
                touchEnabled,
            },
            paintSelection,
            availablePaints,
        },
    } = useFeatureFlaggedPaintPickerContext();

    const logger = useLogger();
    const [imageLoaded, setImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const showDesktopShell =
        !touchEnabled &&
        isEnabled &&
        availablePaints.length > 0 &&
        (isPicking || paintSelection.length < 1);
    const showMobileShell =
        touchEnabled && paintPickerActive && activeImageSrc === props.src;

    const PaintPickerShell = PaintPickerShellV2;
    const PaintPickerMobileShell = PaintPickerMobileShellV2;

    const [isImageError, setIsImageError] = useState(false);
    const reportImageError = (e: ErrorEvent) => {
        logger.error(`Image loading failed on ${window.location.href}`, e);
        console.error(e);
    };

    return (
        <>
            {!!props.useRoccoImage && !isImageError ? (
                <span>
                    <RoccoImage
                        src={props.src}
                        alt={props.alt}
                        className={props.className}
                        width={
                            typeof props.width === 'number'
                                ? props.width
                                : Number(props.width)
                        }
                        height={
                            typeof props.height === 'number'
                                ? props.height
                                : Number(props.height)
                        }
                        priority={props.priority}
                        loading={props.loading}
                        fetchPriority={
                            props.fetchPriority as
                                | 'auto'
                                | 'high'
                                | 'low'
                                | undefined
                        }
                        rel={props.rel}
                        onError={(e) => {
                            reportImageError(e as unknown as ErrorEvent);
                            setIsImageError(true);
                            setImageLoaded(false);
                        }}
                        onLoad={(e) => {
                            imageRef.current = e.target as HTMLImageElement;
                            setImageLoaded(true);
                        }}
                    />
                </span>
            ) : (
                <Image
                    {...props}
                    onLoadingComplete={(result) => {
                        imageRef.current = result;
                        setImageLoaded(true);
                    }}
                    onError={(e) => {
                        reportImageError(e as unknown as ErrorEvent);
                        setIsImageError(true);
                        setImageLoaded(false);
                    }}
                />
            )}
            {imageLoaded && showDesktopShell && (
                <PaintPickerShell
                    imageRef={imageRef.current}
                    imageSrc={props.src}
                />
            )}
            {showMobileShell && (
                <PaintPickerMobileShell imageRef={imageRef.current} />
            )}
        </>
    );
};

export default ImageWithPaintPicker;
